<template>
  <div class="summary">
    <dateP/>
    <div class="container-fluid pt-4 px-4">
      <div class="row g-4">
          <div class="col-sm-6 col-xl-3">
            <SummaryBox
              type="currency"
              iconType="fa-solid fa-chart-line"
              title="Total"
              :amount="getPlatformSummaryTotal()"
            />
          </div>

          <div class="col-sm-6 col-xl-3">
            <SummaryBox
              type="currency"
              iconType="fa-solid fa-chart-column"
              title="Investment"
              :amount="platformSummary.investment"
            />
          </div>

          <div class="col-sm-6 col-xl-3">
            <SummaryBox
              type="currency"
              iconType="fa-solid fa-chart-area"
              title="Profit/Loss"
              :amount="platformSummary.profit_loss"
              :percentage="profitLossPercentage()"
            />
          </div>

          <div class="col-sm-6 col-xl-3">
            <SummaryBox
              type="currency"
              iconType="fa-solid fa-arrow-up"
              title="Dividend"
              :amount="platformSummary.dividend"
            />
          </div>

          <div class="col-sm-12 col-xl-12">
            <!-- render only when overallTotals exists -->
            <PlatformSummaryBox
              v-if="platformStockSummary && platformStockSummary.overallTotals"
              :summary="platformStockSummary.overallTotals"
              :platforms="platformList"
            />
          </div>

          <!-- only try to iterate when platformStockSummary is present -->
          <template v-if="platformStockSummary && platformStockSummary.platformWise">
            <template v-for="(platform, index) of platformStockSummary.platformWise" :key="index">
              <div class="col-sm-6 col-xl-3">
                <PlatformSummaryBox :summary="platform" :platforms="platformList" />
              </div>
            </template>
          </template>
        </div>

        <div class="row g-4">
          <div class="col-sm-6 col-xl-6">
            <BuyBoxList />
          </div>

          <div class="col-sm-6 col-xl-6">
            <SellBoxList />
          </div>
        </div>
    </div>

    <div class="row g-4">
      <div class="col-sm-12 col-xl-12 m-2 p-2">
        <StatisticsChart />
      </div>
    </div>

    <div class="row g-4">
      <div class="col-sm-12 col-xl-6">
        <SummaryTable title="Today Gainer Stock" :row="topGainerStock.row"/>
      </div>

      <div class="col-sm-12 col-xl-6">
        <SummaryTable title="Today Loss Stock" :row="topLossStock.row"/>
      </div>

      <div class="col-sm-12 col-xl-6">
        <SummaryTable title="7 Days Top Gainer Stock" :row="day7TopGainerStock.row"/>
      </div>

      <div class="col-sm-12 col-xl-6">
        <SummaryTable title="7 Days Top Loss Stock" :row="day7TopLossStock.row"/>
      </div>

      <div class="col-sm-12 col-xl-6">
        <SummaryTable title="30 Days Top Gainer Stock" :row="day30TopGainerStock.row"/>
      </div>

      <div class="col-sm-12 col-xl-6">
        <SummaryTable title="30 Days Top Loss Stock" :row="day30TopLossStock.row"/>
      </div>

      <div class="col-sm-12 col-xl-6">
        <SummaryTable title="All Time Top Gainer Stock" :row="allTimeTopGainerStock.row"/>
      </div>

      <div class="col-sm-12 col-xl-6">
        <SummaryTable title="All Time Loss Stock" :row="allTimeTopLossStock.row"/>
      </div>
    </div>
  </div>
</template>

<script>
import { fetchWrapper } from '@/helpers';
import SummaryBox from './Summary/Box.ce.vue';
import PlatformSummaryBox from './Summary/PlatformBox.ce';

import SellBoxList from './Summary/Sell.vue';
import BuyBoxList from './Summary/Buy.vue';
import StatisticsChart from '@/views/dashboard/summary/StatisticsChart.vue';

import SummaryTable from "@/components/Summary/Table.ce.vue";

import { storeToRefs } from 'pinia';
import { useAlertStore, platformStore } from '@/stores';

import gql from 'graphql-tag'

const PLATFORMS_OPTION_QUERY = gql`
  query platformSummary($platformIds: [ID]) {
    platformSummary(platform_ids: $platformIds) {
      investment
      profit_loss
      buy
      sell
      net_sell
      dividend
    }
  }
`;

const PLATFORMS_STOCK_SUMMARY = gql`
  query PlatformStockSummary {
    platformStockSummary {
      platformWise {
        platform_id
        platform_total
        stock_count
        total_stock_up
        total_stock_down
        percent_up
        percent_down
        amount_up
        amount_down
        up_down
        up_down_percent
      }
      overallTotals {
        stock_count
        platform_total
        total_stock_up
        total_stock_down
        percent_up
        percent_down
        amount_up
        amount_down
        up_down
        up_down_percent
      }
    }
  }
`;

export default {
  name: 'Summary-block',
  components: {
    SummaryBox,
    StatisticsChart,
    SellBoxList,
    BuyBoxList,
    SummaryTable,
    PlatformSummaryBox
  },
  setup() {
    const alertStore = platformStore();
    const { platform, platformList } = storeToRefs(alertStore);
    return { 
      alertStore$: useAlertStore(),
      platformOptions: platform,
      platformList: platformList,
    };
  },
  data() {
    return {
      // safe defaults
      platformSummary: {
        investment: 0,
        profit_loss: 0,
        buy: 0,
        sell: 0,
        net_sell: 0,
        dividend: 0
      },
      statisticsChart: [],
      platformStockSummary: {
        platformWise: [],
        overallTotals: null
      },
      mapTypeComponents: [],
      chart: {
        data: {
          labels: [],
          datasets: []
        },
        options: {
          responsive: true  
        }
      },
      topGainerStock: { row: [] },
      topLossStock: { row: [] },
      day7TopGainerStock: { row: [] },
      day7TopLossStock: { row: [] },
      day30TopGainerStock: { row: [] },
      day30TopLossStock: { row: [] },
      allTimeTopGainerStock: { row: [] },
      allTimeTopLossStock: { row: [] },
      summaryData: [],
      stokeList: "",
    }
  },
  methods: {
    // derived values as methods (template calls with ())
    getPlatformSummaryTotal() {
      const inv = Number(this.platformSummary.investment) || 0;
      const pl = Number(this.platformSummary.profit_loss) || 0;
      return inv + pl;
    },

    profitLossPercentage() {
      const inv = Number(this.platformSummary.investment) || 0;
      const pl = Number(this.platformSummary.profit_loss) || 0;
      return inv === 0 ? 0 : Number(((pl * 100) / inv).toFixed(2));
    },

    // robust parser for platformOptions: handles string, array, or Pinia ref-like object
    parsePlatformOptions() {
      const opt = this.platformOptions;
      // if it's a ref-like object with .value, use value
      const raw = opt && typeof opt === 'object' && 'value' in opt ? opt.value : opt;
      if (!raw) return [];
      if (Array.isArray(raw)) return raw;
      try {
        return JSON.parse(raw);
      } catch (e) {
        // not JSON, maybe comma separated or single id
        if (typeof raw === 'string') {
          return raw.split(',').map(s => s.trim()).filter(Boolean);
        }
        return [];
      }
    },

    // kept similar but log and guard platformOptions parsing
    getStockSummaryData() {
      const parsedPlatformIds = this.parsePlatformOptions();

      const calls = [
        {dayCount: 1, target: [this.topGainerStock, this.topLossStock]},
        {dayCount: 7, target: [this.day7TopGainerStock, this.day7TopLossStock]},
        {dayCount: 30, target: [this.day30TopGainerStock, this.day30TopLossStock]},
        {dayCount: 200, target: [this.allTimeTopGainerStock, this.allTimeTopLossStock]},
      ];

      calls.forEach(({dayCount, target}) => {
        fetchWrapper.post(`api/stock/summary`, {
          dayCount,
          platformIds: parsedPlatformIds
        })
        .then((response) => {
          if (response && response.data) {
            target[0].row = response.data.gainers || [];
            target[1].row = response.data.losers || [];
          } else {
            target[0].row = [];
            target[1].row = [];
          }
        })
        .catch(err => {
          console.error(`❌ API failed for dayCount=${dayCount}`, err);
        });
      });
    },
  },

  async mounted() {
    this.getStockSummaryData();
  },

  watch:{
    // watch the pinia ref properly: when platformOptions is a ref, Vue will unwrap in template,
    // but here we simply re-run on change (string/ref both tracked)
    platformOptions: {
      handler: async function () {
        await this.getStockSummaryData();
      },
      deep: false
    }
  },

  apollo: {
    platformSummary: {
      query: PLATFORMS_OPTION_QUERY,
      variables () {
        // guard parsing
        let ids = [];
        try {
          const raw = this.platformOptions && typeof this.platformOptions === 'object' && 'value' in this.platformOptions
            ? this.platformOptions.value
            : this.platformOptions;
          ids = raw ? (Array.isArray(raw) ? raw : JSON.parse(raw)) : [];
        } catch (e) {
          ids = [];
        }
        return { platformIds: ids };
      },
      // assign safely when response comes
      result({ data }) {
        if (data && data.platformSummary) {
          this.platformSummary = {
            investment: data.platformSummary.investment ?? 0,
            profit_loss: data.platformSummary.profit_loss ?? 0,
            buy: data.platformSummary.buy ?? 0,
            sell: data.platformSummary.sell ?? 0,
            net_sell: data.platformSummary.net_sell ?? 0,
            dividend: data.platformSummary.dividend ?? 0
          };
        }
      },
      error(error){
        this.alertStore$.error(JSON.stringify(error.message));
      }
    },
    platformStockSummary: {
      query: PLATFORMS_STOCK_SUMMARY,
      // when data arrives, ensure shape is safe and never null
      result({ data }) {
        const raw = data && data.platformStockSummary ? data.platformStockSummary : null;
        if (raw) {
          this.platformStockSummary = {
            platformWise: Array.isArray(raw.platformWise) ? raw.platformWise : [],
            overallTotals: raw.overallTotals ?? null
          };
        } else {
          // ensure we never set it to null: use safe defaults
          this.platformStockSummary = { platformWise: [], overallTotals: null };
        }
      }
    }
  }
}
</script>